import {useEffect, useMemo, useState} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getInputStepDatasRecord, getPipelineStepRawDatasOfKind} from 'pipelines/StepDataUtils';
import * as ScratchPlateDAO from 'plates/dao/ScratchPlateDAO';

const METADATA_LAYER_NAME = 'metadata';
const LEAF_TYPES_BY_VALUE = new Map<string, LeafType>(
  Object.values(LeafType).map((leafType) => [leafType, leafType])
);

interface LoadedColumns {
  scratchPlateId: string;
  columns: Array<DataFrameColumnDef>;
}

export default function useScratchPlateMappableColumns(step: PipelineStep): Array<DataFrameColumnDef> {
  const plateInputs = useMemo(() => getInputStepDatasRecord(step, PipelineStepDataKind.PLATE), [step]);
  const representativePlateId = step.inputStepDatas
    .filter(({name}) => Object.hasOwn(plateInputs, name))
    .flatMap((stepData) => getPipelineStepRawDatasOfKind(PipelineStepDataKind.PLATE, stepData))
    .at(0)?.id;
  const [loadedColumns, setLoadedColumns] = useState<LoadedColumns | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (representativePlateId == null) {
      return;
    }

    ScratchPlateDAO.listScratchPlateMappableColumns(representativePlateId)
      .then(({layers}) => {
        if (cancelled) {
          return;
        }
        const metadataLayer = layers.find(({layerName}) => layerName === METADATA_LAYER_NAME);
        setLoadedColumns({
          scratchPlateId: representativePlateId,
          columns: (metadataLayer?.columns ?? []).flatMap(({systemName, displayName, leafType}) => {
            const resolvedLeafType = leafType == null ? undefined : LEAF_TYPES_BY_VALUE.get(leafType);
            return resolvedLeafType == null
              ? []
              : [{systemName, displayName, type: {leafType: resolvedLeafType}}];
          }),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadedColumns({scratchPlateId: representativePlateId, columns: []});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [representativePlateId]);

  return representativePlateId != null && loadedColumns?.scratchPlateId === representativePlateId
    ? loadedColumns.columns
    : [];
}
